import ModalProvider from "@repo/components/src/globalProviders/modal";
import { Badge, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Text } from "@repo/components/src/ui/text";
import {
  Toast,
  ToastDescription,
  useToast,
} from "@repo/components/src/ui/toast";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatMoneyFromFragment } from "@repo/components/src/utils/money";
import { WorkspacePlanSettingsQuery } from "@repo/graphql/documents/workspace/query.generated";
import {
  SubscriptionsPlanCodeChoices,
  SubscriptionsPricingNameChoices,
} from "@repo/graphql/gql/graphql";
import { hasValidSubscriptionPayment } from "@/utils/subscriptionStatus";
import { priceFor } from "@/utils/planPrices";
import { router } from "expo-router";
import { Check } from "lucide-react-native";
import ChangePlan from "./changePlan";
import CreditCardModal from "./creditCardModal";

type Props = {
  plan: NonNullable<
    WorkspacePlanSettingsQuery["subscription"]["listPlans"]
  >[number];
  workspace: NonNullable<
    WorkspacePlanSettingsQuery["workspace"]["myWorkspace"]
  >;
  countryId: string | null;
  /** In-app path a paid checkout lands back on (the plan picker's caller). */
  returnPath?: string;
};

const PlanCard = (props: Props) => {
  const { plan: planFragment, workspace, countryId, returnPath } = props;
  const toast = useToast();
  const currentPlan = workspace.workspace.workspaceplan.plan;
  const nextPlan = workspace.workspace.workspaceplan.nextPlan;
  const userWorkspace = workspace;
  const isAccountHolder = userWorkspace.accountHolder;
  const paymentRecurring = workspace.workspace.workspaceplan.paymentrecurring;
  const hasValidPayment = hasValidSubscriptionPayment(
    paymentRecurring?.status?.enum,
  );
  const plan = planFragment;
  const monthlyPrice = priceFor(
    planFragment,
    SubscriptionsPricingNameChoices.Monthly,
    countryId,
  );
  const yearlyPrice = priceFor(
    planFragment,
    SubscriptionsPricingNameChoices.Yearly,
    countryId,
  );
  const isCurrentPlan = currentPlan.id === plan.id;
  const isNextPlan = nextPlan?.id === plan.id;
  const isEnterprise =
    plan.code.enum === SubscriptionsPlanCodeChoices.Enterprise;

  return (
    <Box>
      <VStack space="sm" className="min-h-32">
        <HStack space="sm" className="items-center">
          <Heading className="font-medium shrink">{plan.name}</Heading>
          {isCurrentPlan && (
            <Badge action="success" size="md">
              <BadgeText className="font-medium">ACTUAL</BadgeText>
            </Badge>
          )}
          {isNextPlan && (
            <Badge action="warning" size="md">
              <BadgeText className="font-medium">PRÓXIMO</BadgeText>
            </Badge>
          )}
        </HStack>
        {isEnterprise ? (
          <>
            <Text size="sm" action="muted">
              Escala tu marca con un plan a tu medida.
            </Text>
          </>
        ) : (
          <>
            <Box>
              <Text action="muted" size="xs">
                Mensual
              </Text>
              <Text action="muted" size="sm">
                {formatMoneyFromFragment(monthlyPrice?.totalValue)}
              </Text>
            </Box>
            <Box>
              <Text action="muted" size="xs">
                Anual
              </Text>
              <Text action="muted" size="sm">
                {formatMoneyFromFragment(yearlyPrice?.totalValue)}
              </Text>
            </Box>
          </>
        )}
      </VStack>
      <Box
        className={`mt-4 min-h-16 ${!isAccountHolder || nextPlan ? "hidden" : ""}`}
      >
        <Button
          variant="outline"
          className="w-fit"
          size="xs"
          action="brand"
          rounded="lg"
          hidden={isCurrentPlan && hasValidPayment}
          onPress={() => {
            if (monthlyPrice && yearlyPrice && !isEnterprise) {
              if (!hasValidPayment) {
                ModalProvider.show({
                  children: (
                    <CreditCardModal
                      monthlyPrice={monthlyPrice}
                      yearlyPrice={yearlyPrice}
                      plan={planFragment}
                      returnPath={returnPath}
                    />
                  ),
                  size: "lg",
                  contentProps: {
                    className: "rounded-2xl",
                  },
                });
              } else if (paymentRecurring) {
                // `hasValidPayment` already implies a subscription exists;
                // the explicit check is what lets ChangePlan take a non-null
                // one instead of widening its prop.
                ModalProvider.show({
                  children: (
                    <ChangePlan
                      price={monthlyPrice}
                      plan={planFragment}
                      paymentRecurring={paymentRecurring}
                      onSuccess={() => {
                        toast.show({
                          id: String(Math.random()),
                          placement: "bottom",
                          duration: 8000,
                          render: ({ id }) => (
                            <Toast
                              nativeID={`toast-${id}`}
                              action="success"
                              variant="solid"
                            >
                              <ToastDescription>
                                Has cambiado al plan {plan.name} exitosamente.
                              </ToastDescription>
                            </Toast>
                          ),
                        });
                      }}
                    />
                  ),
                  size: "lg",
                  contentProps: {
                    className: "rounded-2xl",
                  },
                });
              }
            } else if (isEnterprise) {
              router.replace(
                "https://calendly.com/gonzalo-montu/montu-conoce-enterprise",
              );
            }
          }}
        >
          <ButtonText>{isEnterprise ? "Contactar" : "Seleccionar"}</ButtonText>
        </Button>
      </Box>
      <VStack space="sm" className="m">
        {plan.characteristics.map((characteristic) => {
          return (
            <HStack space="sm" key={characteristic} className="items-start">
              <Icon as={Check} action="success" />
              <Text action="muted" size="sm">
                {characteristic}
              </Text>
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
};

export default PlanCard;
